/**
 * vehicleCsv.ts — 車両一覧の CSV 書き出し（2026-09-17）
 *
 * /vehicle_list の「CSV」ボタンから使う。車両マスタを表計算で扱いたい
 * （棚卸し・車検やリース満了の管理・保険会社への提出など）ための出力で、
 * 画面に出ていない detail（所属・所有者・整備日・車検満了…）も全部出す。
 *
 * 方針:
 *   - 列は固定の基本情報 + チェック項目（マスタから動的） + detail の順
 *   - 真偽は「済/未」「有効/無効」のように日本語で出す（Excel で読む前提）
 *   - 日時は ISO のまま出さず YYYY/MM/DD HH:mm に揃える
 *   - 色コード・内部用 json_data（返却送迎）は出さない。人が読む用途では無意味なため
 */
import dayjs from 'dayjs';

export interface VehicleCheckMaster {
  id: number;
  name: string;
  sort_order?: number;
}

/** 車両 json_data のうち、画面の内部状態として使っていて CSV には出さないキー */
const JSON_DATA_INTERNAL_KEYS = new Set(['返却送迎']);
/** 色は人が読んでも意味が無いので出さない */
const JSON_DATA_COLOR_KEYS = new Set(['bg_color', 'text_color']);

const fmtDateTime = (v: unknown): string => {
  if (!v) return '';
  const d = dayjs(String(v));
  return d.isValid() ? d.format('YYYY/MM/DD HH:mm') : String(v);
};

const fmtDate = (v: unknown): string => {
  if (!v) return '';
  const d = dayjs(String(v));
  return d.isValid() ? d.format('YYYY/MM/DD') : String(v);
};

const yesNo = (v: unknown, yes: string, no: string): string => (v === true ? yes : no);

/** RFC4180 準拠のエスケープ。カンマ・改行・引用符を含むときだけ引用する */
export const csvCell = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** detail オブジェクトの列。API 仕様 v3.9.0 の順序に合わせる */
const DETAIL_COLUMNS: Array<[key: string, label: string, kind: 'text' | 'date']> = [
  ['affiliation', '所属', 'text'],
  ['owner', '所有者', 'text'],
  ['user', '使用者', 'text'],
  ['regist_status', '登録状態', 'text'],
  ['tire_size', 'タイヤサイズ', 'text'],
  ['tire', 'タイヤ交換日', 'date'],
  ['oil', 'オイル交換日', 'date'],
  ['wax', 'WAX施工日', 'date'],
  ['battery', 'バッテリー交換日', 'date'],
  ['additive', '添加剤施工日', 'date'],
  ['half_year_inspection', '6ヶ月点検日', 'date'],
  ['inspection_end', '車検満了日', 'date'],
  ['lease_end_date', 'リース満了日', 'date'],
  ['memo', '詳細メモ', 'text'],
];

/**
 * 車両配列 → CSV 文字列（BOM 無し。呼び出し側で付ける）
 *
 * @param vehicles  API の車両オブジェクト（check_situations / detail / json_data を含む）
 * @param checks    車両チェック項目マスタ。列の順序と名前に使う。
 *                  車両側の check_situations に無い項目は「未」扱い
 */
export const buildVehicleCsv = (vehicles: any[], checks: VehicleCheckMaster[]): string => {
  const sortedChecks = [...checks].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  // json_data.data のキーは車両ごとに違いうるので、全車両を見て列を決める
  const jsonKeys: string[] = [];
  vehicles.forEach(v => {
    Object.keys(v?.json_data?.data || {}).forEach(k => {
      if (JSON_DATA_INTERNAL_KEYS.has(k) || JSON_DATA_COLOR_KEYS.has(k)) return;
      if (!jsonKeys.includes(k)) jsonKeys.push(k);
    });
  });
  const jsonLabel = (k: string) => ({ label: 'ラベル', mark: 'マーク' } as Record<string, string>)[k] || k;

  const header = [
    '車両ID', '車両コード', 'ナンバープレート',
    '車両タイプコード', '車両タイプ名', 'カテゴリ', '定員',
    '車両ステータス', '移動ステータス',
    '利用外', '有効', '有効開始日', '有効終了日', '表示優先度',
    'メモ',
    ...sortedChecks.map(c => `チェック:${c.name}`),
    '最終確認日時', '最終確認者ID',
    ...DETAIL_COLUMNS.map(([, label]) => label),
    ...jsonKeys.map(jsonLabel),
    '作成日時', '更新日時',
  ];

  const rows = vehicles.map(v => {
    const vt = v.vehicle_type || {};
    const detail = v.detail || {};
    const situations: any[] = Array.isArray(v.check_situations) ? v.check_situations : [];
    const checkCells = sortedChecks.map(c => {
      const s = situations.find(x => x.id === c.id);
      return s?.value === true ? '済' : '未';
    });
    return [
      v.id,
      v.vehicle_code,
      v.vehicle_number,
      vt.vehicle_type_code,
      vt.vehicle_type_name,
      vt.vehicle_type_category,
      vt.vehicle_type_capacity,
      v.status_name,
      v.location_status_name,
      yesNo(v.out_of_use, '利用外', ''),
      v.is_active === false ? '無効' : '有効',
      fmtDate(v.active_from),
      fmtDate(v.active_to),
      v.reservation_box_priority,
      v.memo,
      ...checkCells,
      fmtDateTime(v.last_checked_at),
      v.last_checked_by,
      ...DETAIL_COLUMNS.map(([key, , kind]) => (kind === 'date' ? fmtDate(detail[key]) : detail[key])),
      ...jsonKeys.map(k => v?.json_data?.data?.[k]),
      fmtDateTime(v.created_at),
      fmtDateTime(v.updated_at),
    ];
  });

  return [header, ...rows].map(r => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
};

/** ブラウザでダウンロードさせる。Excel で文字化けしないよう UTF-8 BOM を付ける */
export const downloadCsv = (csv: string, filename: string): void => {
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
